import { useEffect, useMemo, useRef, useState } from 'react';
import { pickFromPool, type CaseDef, type ViewId } from '../state';
import { api, newIdempotencyKey } from '../lib/api';

type Props = {
    caseDef: CaseDef;
    balance: number;
    onNavigate: (id: ViewId) => void;
    onWin: (value: number, cost: number) => void;
};

type Cell = { value: number; tier: 'blue' | 'yellow' | 'green' | 'orange' | 'red' };

/**
 * Roulette over a 5x5 grid. Animation behaviour:
 *  1. On tap, scramble the pool into 25 visible cells.
 *  2. Walk a random cursor over the grid, slowing down each beat (80→150→300 ms).
 *  3. Stop after ~4 s, fade non-winning cells, fire onWin().
 *
 * The visible cells are derived from the pool deterministically (shuffled
 * once per round), so the value the user sees and the value we emit always
 * match: important because the spec lists exact prize counts per case.
 */
export function BoxGameView({ caseDef, balance, onNavigate, onWin }: Props) {
    const [cells, setCells] = useState<Cell[]>(() => buildCells(caseDef));
    const [highlight, setHighlight] = useState<number | null>(null);
    const [phase, setPhase] = useState<'idle' | 'rolling' | 'reveal'>('idle');
    const [winner, setWinner] = useState<number | null>(null);

    const intervalRef = useRef<number | null>(null);
    const timeoutsRef = useRef<number[]>([]);

    useEffect(
        () => () => {
            if (intervalRef.current) window.clearInterval(intervalRef.current);
            timeoutsRef.current.forEach((id) => window.clearTimeout(id));
        },
        [],
    );

    const canPlay = balance >= caseDef.price && phase === 'idle';

    const start = async () => {
        if (!canPlay) return;
        let rolled = buildCells(caseDef);
        setCells(rolled);
        setPhase('rolling');
        setWinner(null);

        // Ask the server to deal the open. The balance is debited and the
        // payout credited atomically inside this call — by the time it
        // resolves the actual settlement has already happened, regardless
        // of whether the user later closes the app mid-animation.
        let serverPayout: number | null = null;
        let serverColor: string | null = null;
        try {
            const r = await api.openBox(caseDef.id, newIdempotencyKey());
            serverPayout = r.payout;
            serverColor = r.color;
        } catch {
            /* offline / unauthorized → local mock */
        }

        // Inject the server's authoritative value+tier into the grid so the
        // animation has somewhere truthful to land. Earlier the cell shown
        // could differ from the credited amount when the random local grid
        // didn't happen to include the server's payout — the user saw
        // "120" but got "250" credited and naturally complained the
        // displayed numbers don't match the win.
        if (serverPayout !== null && serverColor !== null) {
            const match = rolled.findIndex(
                (c) => c.tier === serverColor && c.value === serverPayout,
            );
            if (match < 0) {
                const injectIdx = Math.floor(Math.random() * rolled.length);
                rolled = rolled.slice();
                rolled[injectIdx] = { value: serverPayout, tier: serverColor as Cell['tier'] };
                setCells(rolled);
            }
        }

        let speed = 80;
        let elapsed = 0;
        let last = -1;

        const step = () => {
            const next = Math.floor(Math.random() * 25);
            setHighlight(next);
            last = next;
            elapsed += speed;

            if (elapsed > 2500) speed = 150;
            if (elapsed > 3500) speed = 300;

            if (intervalRef.current) window.clearInterval(intervalRef.current);
            if (elapsed >= 4000) {
                let finalIdx = last;
                if (serverPayout !== null && serverColor !== null) {
                    const match = rolled.findIndex(
                        (c) => c.tier === serverColor && c.value === serverPayout,
                    );
                    if (match >= 0) finalIdx = match;
                }
                setPhase('reveal');
                setWinner(finalIdx);
                const winningCell = rolled[finalIdx];
                const prize = serverPayout ?? (winningCell ? winningCell.value : 0);
                const tid = window.setTimeout(() => {
                    onWin(prize, caseDef.price);
                }, 700);
                timeoutsRef.current.push(tid);
                return;
            }
            intervalRef.current = window.setInterval(step, speed);
        };

        intervalRef.current = window.setInterval(step, speed);
    };

    const titleColor = useMemo(() => {
        return caseDef.color === 'green'
            ? 'var(--green)'
            : caseDef.color === 'blue'
              ? 'var(--blue)'
              : 'var(--red)';
    }, [caseDef.color]);

    return (
        <div className="view">
            <h2 className="text-center fw-900" style={{ color: titleColor, fontSize: 20, letterSpacing: 2 }}>
                {caseDef.title}
            </h2>
            <p className="text-center text-muted f-12">
                ПОЛЕ 5x5 · ЦЕНА {caseDef.price} BSP
            </p>

            <div className="box-grid-wrapper">
                <div className="box-grid">
                    {cells.map((cell, idx) => {
                        const isWinner = phase === 'reveal' && winner === idx;
                        const isHl = phase === 'rolling' && highlight === idx;
                        const isDim = phase === 'reveal' && winner !== idx;
                        const isHidden = phase === 'idle';
                        const cls = [
                            'box-cell',
                            isHidden ? 'qmark' : `tier-${cell.tier}`,
                            isHl ? 'hl' : '',
                            isWinner ? 'hl' : '',
                            isDim ? 'dim' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');
                        return (
                            <div key={idx} className={cls}>
                                {isHidden ? '?' : cell.value}
                            </div>
                        );
                    })}
                </div>

                {phase === 'idle' && (
                    <div className="play-overlay">
                        <button
                            type="button"
                            className="play-btn-circle"
                            onClick={start}
                            disabled={!canPlay}
                            style={{ opacity: canPlay ? 1 : 0.4 }}
                        >
                            <span style={{ fontSize: 14 }}>ИГРАТЬ</span>
                            <span style={{ fontSize: 10, marginTop: 2 }}>
                                ({caseDef.price} BSP)
                            </span>
                        </button>
                    </div>
                )}
            </div>

            {!canPlay && phase === 'idle' && balance < caseDef.price && (
                <div className="withdraw-warn">Недостаточно BSP для игры.</div>
            )}

            <button
                type="button"
                className="back-btn"
                onClick={() => onNavigate('boxes')}
                disabled={phase === 'rolling'}
            >
                НАЗАД К КЕЙСАМ
            </button>
        </div>
    );
}

function buildCells(c: CaseDef): Cell[] {
    const arr: Cell[] = [];
    for (let i = 0; i < 25; i++) {
        // Pool is always non-empty (built from spec counts: 15+8+4+2+1).
        const p = pickFromPool(c) as { value: number; tier: Cell['tier'] };
        arr.push({ value: p.value, tier: p.tier });
    }
    return arr;
}
